// [S-060a] Application shell — sidebar (left) + top bar + main content.
// Sub-routes provide their own breadcrumb + body via <TopBar/> and
// <main className="main__body">. The shell deliberately stays thin so
// each screen can choose how dense its layout is.
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

// [rail epic] Exposes AppShell's S-399 sidebar-collapse state to descendants
// — Home's rail auto-collapses the sidebar to reclaim width when a
// draft/template/precedent opens, and restores it on close. `null` outside
// AppShell (every real route mounts inside it, so this only matters for
// tests that render a screen standalone) — those callers just no-op rather
// than crash.
const SidebarCollapseContext = createContext<{
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
} | null>(null);

export function useSidebarCollapse(): {
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
} {
  return (
    useContext(SidebarCollapseContext) ?? { collapsed: false, setCollapsed: () => {} }
  );
}

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { pathname } = useLocation();
  // S-381 — mobile nav drawer. Desktop ignores this state entirely (the
  // drawer CSS only applies below --cs-bp-md); on phones the sidebar is
  // off-canvas and this toggles it.
  const [navOpen, setNavOpen] = useState(false);

  // S-399 — desktop sidebar hide/show, persisted so it survives reloads.
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem("cs.sidebarCollapsed") === "1"
  );
  useEffect(() => {
    localStorage.setItem("cs.sidebarCollapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  // Close the drawer on any route change — covers NavLink taps and
  // browser back/forward, so the drawer never lingers over the new page.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  // Escape closes the drawer while it's open.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  const shellClass = [
    "shell",
    navOpen && "shell--nav-open",
    collapsed && "shell--collapsed",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <SidebarCollapseContext.Provider value={{ collapsed, setCollapsed }}>
      <div className={shellClass}>
        <Sidebar />
        {/* Scrim — inert/hidden on desktop; covers content when the drawer
            is open on mobile. A <button> so it's keyboard-dismissable. */}
        <button
          type="button"
          className="shell__scrim"
          aria-label="Close navigation"
          tabIndex={navOpen ? 0 : -1}
          onClick={() => setNavOpen(false)}
        />
        <main className="main">
          <TopBar
            crumb={crumbFor(pathname)}
            navOpen={navOpen}
            onMenuToggle={() => setNavOpen((v) => !v)}
            sidebarCollapsed={collapsed}
            onToggleSidebar={() => setCollapsed((v) => !v)}
          />
          <div className="main__body">{children}</div>
        </main>
      </div>
    </SidebarCollapseContext.Provider>
  );
}

function crumbFor(pathname: string): string {
  if (pathname.startsWith("/library/")) return "Templates / template";
  if (pathname === "/briefs/new") return "Pre-negotiation briefs / compose";
  if (pathname.startsWith("/briefs/")) return "Pre-negotiation briefs / brief";
  switch (pathname) {
    case "/":
    case "/home":
      return "Home";
    case "/library":
      return "Templates";
    case "/precedent":
      return "Precedent";
    case "/briefs":
      return "Pre-negotiation briefs";
    case "/activity":
      return "Activity";
    case "/index-health":
      return "Search status";
    case "/settings":
      return "Settings";
    case "/reattestation":
      return "Re-approval schedule";
    default:
      return "—";
  }
}
